import { useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { Loader2, MapPin } from "lucide-react";

/**
 * Nominatim address autocomplete.
 * Debounced (300ms), max 5 results, US-only, free OpenStreetMap API.
 *
 * Props:
 *  - value: current input string
 *  - onChange(string): called as user types
 *  - onSelect({ address, lat, lng }): called when a result is picked
 *  - placeholder, testid, disabled
 */
export default function AddressAutocomplete({
  value,
  onChange,
  onSelect,
  placeholder = "Search address…",
  testid,
  disabled = false,
}) {
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);
  const containerRef = useRef(null);
  const abortRef = useRef(null);

  // Click outside to close
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const runSearch = (q) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!q || q.trim().length < 3) {
      setResults([]);
      setLoading(false);
      setOpen(false);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        if (abortRef.current) abortRef.current.abort();
        abortRef.current = new AbortController();
        const url = `https://nominatim.openstreetmap.org/search?format=json&limit=5&addressdetails=0&countrycodes=us&q=${encodeURIComponent(
          q
        )}`;
        const res = await fetch(url, {
          headers: { "Accept-Language": "en" },
          signal: abortRef.current.signal,
        });
        const data = await res.json();
        setResults(Array.isArray(data) ? data : []);
        setOpen(true);
      } catch (err) {
        if (err?.name !== "AbortError") {
          console.error("Nominatim search failed", err);
          setResults([]);
        }
      } finally {
        setLoading(false);
      }
    }, 300);
  };

  const handleChange = (e) => {
    const q = e.target.value;
    onChange?.(q);
    runSearch(q);
  };

  const pick = (r) => {
    const sel = {
      address: r.display_name,
      lat: parseFloat(r.lat),
      lng: parseFloat(r.lon),
    };
    onSelect?.(sel);
    setOpen(false);
    setResults([]);
  };

  return (
    <div className="relative" ref={containerRef}>
      <Input
        value={value || ""}
        onChange={handleChange}
        onFocus={() => results.length > 0 && setOpen(true)}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete="off"
        data-testid={testid}
      />
      {loading && (
        <Loader2
          size={14}
          className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 animate-spin pointer-events-none"
        />
      )}
      {open && (results.length > 0 || loading) && (
        <div
          className="absolute z-[1100] mt-1 w-full bg-white border border-slate-200 rounded-md shadow-lg max-h-72 overflow-y-auto"
          data-testid={testid ? `${testid}-results` : undefined}
        >
          {loading && results.length === 0 && (
            <div className="px-3 py-2 text-xs text-slate-500">Searching…</div>
          )}
          {results.map((r) => (
            <button
              key={r.place_id}
              type="button"
              onClick={() => pick(r)}
              className="w-full text-left px-3 py-2 text-sm hover:bg-orange-50 hover:text-[#E8650A] border-b border-slate-50 last:border-b-0 flex items-start gap-2"
              data-testid={testid ? `${testid}-result-${r.place_id}` : undefined}
            >
              <MapPin size={14} className="text-[#E8650A] mt-0.5 flex-shrink-0" />
              <span className="line-clamp-2">{r.display_name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
